//React
import React, { useEffect } from "react";
import { Routes, Route, useLocation, Outlet, Navigate } from "react-router-dom";
import Header from "./components/header";
import Home from "./views/home";
import Manager from "./views/manager";
import "./styles/primereact.scss"; // PrimeReact theme overrides (app loads no PrimeReact theme)
import Arctrax from "./views/arctrax";
import DashboardNotFound from "./views/insights/dashboardNotFound"
import Curated from "./views/curated";
import Help from "./views/help";
import Utilities from "./views/utilities";
import Profile from "./views/profile";
import AdQualityAnalyzer from "./views/adQuality";
import ArcLens from "./views/arcLens";
import AIAudienceExplorerV2 from "./views/aiAudienceExplorerV2";
import MarcoChatWidget from "./components/marcoChatWidget";
import Settings from "./views/settings";
import Footer from "./components/footer";
import Privacy from "./views/privacy";
import PropTypes from "prop-types";
import {ModeledEdit, ModeledView} from "./views/modeling";
import {SignalEdit, SignalView} from "./views/signal";
import LegacyBuilderRedirect from "./views/builder-v2/legacyRedirect";
import BuilderV2 from "./views/builder-v2";
import BuilderV2Edit from "./views/builder-v2/edit";
import BuilderV2View from "./views/builder-v2/view";
import InsightsDeck from "./views/insights-deck";
//CSS
import "./App.scss";
import Cookies from "./views/cookies";
import Terms from "./views/terms";
import Contact from "./views/contact";
import LoginPage from "./views/login";

import PasswordRecovery from "./views/password-recovery";
import DebugAudienceGenerate from "./views/debugAudienceGenerate";
import AdminAIGenerations from "./views/admin/aiGenerations";
import AdminAIGenerationDetail from "./views/admin/aiGenerations/detail";
import AdminDashboards from "./views/admin/dashboards";

import LogRocket from 'logrocket';
import JwtDecode from "jwt-decode";
import InsightsV2 from "./views/insights/demo";
import { tokenRefreshService } from "./services/tokenRefresh";
import { getToken, getSession } from "./services/auth";

function Layout({ hideHeaderPaths = [] }) {
	const { pathname } = useLocation();
	return (
		<>
			{!hideHeaderPaths.includes(pathname) && <Header />}
			<Outlet />
			{!hideHeaderPaths.includes(pathname) && <Footer />}
		</>
	);
}
function App() {
	useEffect(() => {
		if (!window.__RUNTIME_CONFIG__.LOGROCKET_APP.endsWith("local")) {
			LogRocket.init(window.__RUNTIME_CONFIG__.LOGROCKET_APP, {
				mergeIframes: true,
				childDomains: [
					window.location.origin
				]
			});
		}
	}, []);

	useEffect(() => {
		if (!window.__RUNTIME_CONFIG__.LOGROCKET_APP.endsWith("local")) {
			const session = getSession();
			if (session && session.access_token) {
				const tokenData = JwtDecode(session.access_token);
				const userId = tokenData.user_id;
				const name = tokenData.username;
				const email = tokenData.email;
				const publisher = session.publisher_name;
				const isArcSpan = publisher === "ArcSpan";
				const isClient = !isArcSpan;
				const role = session.role_name;
				const amsFlavor = session.ams_flavor;
				LogRocket.identify(userId.toString(), {
					name: name,
					email: email,
					publisher: publisher,
					isArcSpan: isArcSpan,
					isClient: isClient,
					role: role,
					amsFlavor: amsFlavor
				});
			}
		}
	});

	// Initialize token refresh service on app load
	useEffect(() => {
		const token = getToken();
		if (token) {
			tokenRefreshService.initialize(token);
			console.log('Token refresh service initialized');
		}
	}, []);

	// eslint-disable-next-line no-unused-vars
	const location = useLocation();

	return (
		<div className="App">
			<MarcoChatWidget />
			<Routes>
				<Route
					path="/ad-quality"
					element={<AdQualityAnalyzer />}
				/>
				<Route
					path="/arclens"
					element={<ArcLens />}
				/>
				<Route element={<Layout hideHeaderPaths={["/login", "/password-recovery", "/404.html", "/ad-quality", "/arclens"]} />}>
					<Route
						path="/"
						element={<Home />}
					/>
					<Route
						path="/login"
						element={<LoginPage />}
					/>
					<Route
						path="/audience-manager"
						element={<Manager />}
					/>
					{/* Legacy V1 builder URLs (ASB-3706): permanent client-side redirects to V2 */}
					<Route
						path="/audience-builder"
						element={<LegacyBuilderRedirect />}
					/>
					<Route
						path="/audience-builder/edit/:id"
						element={<LegacyBuilderRedirect mode="edit" />}
					/>
					<Route
						path="/audience-builder/view/:id"
						element={<LegacyBuilderRedirect mode="view" />}
					/>
					<Route
						path="/audience-builder-v2"
						element={<BuilderV2 />}
					/>
					<Route
						path="/audience-builder-v2/edit/:id"
						element={<BuilderV2Edit />}
					/>
					<Route
						path="/audience-builder-v2/view/:id"
						element={<BuilderV2View />}
					/>
					<Route
						path="/insights-deck"
						element={<InsightsDeck />}
					/>
					<Route
						path="/audience-modeling/edit/:id"
						element={<ModeledEdit />}
					/>
					<Route
						path="/audience-modeling/view/:id"
						element={<ModeledView />}
					/>
					<Route
						path="/audience-signal/edit/:id"
						element={<SignalEdit />}
					/>
					<Route
						path="/audience-signal/view/:id"
						element={<SignalView />}
					/>
					<Route
						path="/audience-insights/:id"
						element={<InsightsV2 />}
					/>
					<Route
						path="/audience-insights"
						element={<InsightsV2 />}
					/>
					<Route
						path="/audience-arclens"
						element={<ArcLens />}
					/>
					<Route
						path="/ai-audience-explorer"
						element={<Navigate to="/ai-audience-explorer-v2" replace />}
					/>
					<Route
						path="/demo/ai-audience-explorer"
						element={<Navigate to="/demo/ai-audience-explorer-v2" replace />}
					/>
					<Route
						path="/ai-audience-explorer-v2"
						element={<AIAudienceExplorerV2 />}
					/>
					<Route
						path="/ai-audience-explorer-v2/results/:generationId"
						element={<AIAudienceExplorerV2 />}
					/>
					<Route
						path="/demo/ai-audience-explorer-v2"
						element={<AIAudienceExplorerV2 demoMode={true} />}
					/>
					<Route
						path="/404.html"
						element={<DashboardNotFound />}
					/>
					<Route
						path="/arcspan-curated"
						element={<Curated />}
					/>
					<Route
						path="/arctrax-reach"
						element={<Arctrax />}
					/>
					<Route
						path="/help/*"
						element={<Help />}
					/>
					<Route
						path="/utilities"
						element={<Utilities />}
					/>
					<Route
						path="/settings/:id"
						element={<Settings />}
					/>
					<Route
						path="/settings"
						element={<Settings />}
					/>
					<Route
						path="/profile"
						element={<Profile />}
					/>
					<Route
						path="/cookies"
						element={<Cookies />}
					/>
					<Route
						path="/privacy"
						element={<Privacy />}
					/>
					<Route
						path="/terms"
						element={<Terms />}
					/>
					<Route
						path="/contact"
						element={<Contact />}
					/>
					<Route
						path="/password-recovery"
						element={<PasswordRecovery />}
					/>
					<Route
						path="/debug/audience-generate"
						element={<DebugAudienceGenerate />}
					/>
					<Route
						path="/admin/ai-generations"
						element={<AdminAIGenerations />}
					/>
					<Route
						path="/admin/ai-generations/:generationId"
						element={<AdminAIGenerationDetail />}
					/>
					{/* Admin Dashboards (ASB-4377): the landing page groups the panels by topic; a panel id opens one */}
					<Route
						path="/admin/dashboards"
						element={<AdminDashboards />}
					/>
					<Route
						path="/admin/dashboards/:panelId"
						element={<AdminDashboards />}
					/>
				</Route>
			</Routes>
		</div>
	);
}
Layout.propTypes = {
	hideHeaderPaths: PropTypes.array,
};

App.propTypes = {
	hideHeaderPaths: PropTypes.array,
};

export default App;
